import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Box,
  Modal,
  FormHelperText,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import closeIcon from "../../assets/images/closeicon.svg";
import checkImg from "../../assets/images/check_circle.svg";
import AccessibleTooltip from "../Tooltip/Tooltip";
import ComboBox from "../ComboBox";
import PropTypes from "prop-types";
import { AddUserAction, EditUserAction } from "../../Actions/AddEditUserAction";
import {
  handleValidation,
  buildAddPayload,
  buildEditPayload,
  handleUserError,
} from "../../utils/addEditUserUtils.js";
import { fieldUser } from "./FieldJson";
import "./mustyle.scss";
import "../../assets/styles/form.scss";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  paddingLeft: 4,
  paddingRight: 4,
};

const AddEditUser = (props) => {
  const userState = useSelector((state) => state.usersState);
  const dispatch = useDispatch();
  const {
    setOpen,
    open,
    actionButton,
    setActionButton,
    editData,
    handleClose: handleCloseParentMethod,
  } = props;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roleAssignment: "",
    status: "",
  });
  const [formError, setFormError] = useState({});
  const [successMessage, setSuccessMessage] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const firstInputRef = useRef(null);
  const token = JSON.parse(localStorage.getItem("token"));
  let decodedToken = "";
  if (token) {
    decodedToken = jwtDecode(token);
  }

  // Focus handling
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (open && firstInputRef.current) firstInputRef.current.focus();
    }, 200);
    return () => clearTimeout(timeout);
  }, [open]);

  // Escape key tooltip close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === "Escape" || e.key === "Esc") && tooltipOpen) {
        setTooltipOpen(false);
        e.preventDefault();
        e.stopPropagation();
      }
    };
    if (open) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, tooltipOpen]);

  // Input change
  const handleInputChange = (e) => {
    const name = e.target ? e.target.name : e.name;
    const value = e.target ? e.target.value : e.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Add / Edit
  const handleAdd = (e) => {
    e.preventDefault();
    const payload = buildAddPayload(formData);
    dispatch(AddUserAction(payload));
  };

  const handleEdit = () => {
    const payload = buildEditPayload(formData, editData);
    dispatch(EditUserAction(payload, editData?.userid));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!handleValidation(formData, actionButton, setFormError)) return;
    actionButton === "add" ? handleAdd(e) : handleEdit();
  };

  // Handle API result
  useEffect(() => {
    if (!userState.isLoading && userState.data !== null) {
      setSuccessMessage(true);
    } else {
      handleUserError(userState, actionButton, setFormError);
    }
  }, [userState]);

  // Prefill edit form
  useEffect(() => {
    if (actionButton === "edit" && editData) {
      setFormData({
        firstName: editData.name?.split(" ")[0] || "",
        lastName: editData.name?.split(" ")[1] || "",
        email: editData.emailaddress || "",
        roleAssignment: editData.role || "",
        status: editData.status === "ACTIVE" ? "ACTIVE" : "INACTIVE",
      });
    } else {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        roleAssignment: "",
        status: "",
      });
    }
  }, [actionButton, editData]);

  const handleClose = () => {
    setOpen(false);
    handleCloseParentMethod();
    setActionButton("");
    setFormError({});
    setSuccessMessage(false);
    setTooltipOpen(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      roleAssignment: "",
      status: "",
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={tooltipOpen}
      className={`globalForm ${successMessage ? "userSuccessAdded" : ""}`}
    >
      <div role="none">
        <Box role="dialog" sx={style}  aria-labelledby="add-edit-user-title" className="mainModalBox"  aria-modal="true">
          <span aria-live="polite" className="sr-only" data-testId="errors">
            {Object.values(formError).filter(Boolean).join(", ")}
          </span>

          {/* Header */}
          <div
            className="Modal_header"
            style={
              successMessage
                ? { display: "flex", justifyContent: "flex-end", marginTop: "15px" }
                : {}
            }
          >
            {!successMessage && (
              <h1 id="add-edit-user-title">
                {actionButton === "edit" ? "Edit User" : "Create New User"}
              </h1>
            )}
            <AccessibleTooltip
              title="Close"
              open={tooltipOpen}
              customclass={
                successMessage
                  ? "modalcloseTooltip sucessModaltooltip"
                  : "modalcloseTooltip"
              }
            >
              <button
                ref={firstInputRef}
                onClick={handleClose}
                className="close_button_modal"
                aria-label="Close"
                onFocus={() => setTooltipOpen(true)}
                onBlur={() => setTooltipOpen(false)}
                onMouseEnter={() => setTooltipOpen(true)}
                onMouseLeave={() => setTooltipOpen(false)}
              >
                <img src={closeIcon} alt="" aria-hidden="true" />
              </button>
            </AccessibleTooltip>
          </div>

          {/* Middle */}
          <div className="Modal_middle" id="add-edit-user-description">
            {!successMessage ? (
              <Box component="form" noValidate autoComplete="off">
                {fieldUser.map((data) => {
                  const isInput = data.type === "input";
                  const isSelectable =
                    data.type === "select" &&
                    (actionButton !== "add" || data.name !== "status");

                  if (isInput) {
                    return (
                      <TextField
                        key={data.id || data.name}
                        id={data.id}
                        name={data.name}
                        label={data.label}
                        variant="outlined"
                        onChange={handleInputChange}
                        value={formData[data.name] || ""}
                        error={!!formError[data.name]}
                        className="innerFields"
                        helperText={formError[data.name] || data.helperText}
                        fullWidth
                        sx={{ "& .MuiInputLabel-root": { transform: "inherit" } }}
                      />
                    );
                  }
                  if (isSelectable) {
                    return (
                      <FormControl key={data.id || data.name} fullWidth className="innerFields">
                        <InputLabel
                          variant="standard"
                          htmlFor={data.id}
                          shrink={false}
                          className="dropDownLabel"
                        >
                          {data.label}
                        </InputLabel>
                        <ComboBox
                          label={
                            data.name === "roleAssignment"
                              ? "Select Role"
                              : "Select Status"
                          }
                          options={
                            data.name === "roleAssignment"
                              ? decodedToken.userRole === 'BUILDING_ADMIN' ? ["Building Admin", "Building Viewer"] : ["Admin", "Viewer", "Building Admin", "Building Viewer"]
                              : ["ACTIVE", "INACTIVE"]
                          }
                          value={formData[data.name] || ""}
                          onChange={(value) =>
                            handleInputChange({
                              name: data.name,
                              value,
                            })
                          }
                          classes="customCombo"
                        />
                        <FormHelperText id={`${data.id}-helper-text`}>
                          {formError[data.name]}
                        </FormHelperText>
                      </FormControl>
                    );
                  }
                  return null;
                })}
              </Box>
            ) : (
              <div className="emailsent-wrapper manageUser_details" role="alert">
                <img src={checkImg} alt="" aria-hidden="true" />
                <h2>Success</h2>
                <p>
                  User has {actionButton === "add" ? "created" : "updated"} successfully.
                </p>
                <span className="returnUser">
                  Return to{" "}
                  <button
                    type="button"
                    className="btnSubmit mu_back"
                    onClick={handleClose}
                  >
                    Manage User
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="Modal_footer">
            {!successMessage && (
              <Box sx={{ display: "flex" }}>
                <Button
                  variant="contained"
                  className="saveBtn"
                  type="submit"
                  onClick={handleSubmit}
                  aria-label="Save"
                >
                  Save
                </Button>
              </Box>
            )}
          </div>
        </Box>
      </div>
    </Modal>
  );
};

AddEditUser.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  actionButton: PropTypes.oneOf(["add", "edit"]).isRequired,
  setActionButton: PropTypes.func.isRequired,
  editData: PropTypes.shape({
    userid: PropTypes.number,
    name: PropTypes.string,
    emailaddress: PropTypes.string,
    role: PropTypes.string,
    status: PropTypes.string,
    telephone: PropTypes.string,
  }),
  handleClose: PropTypes.func.isRequired,
};

export default AddEditUser;
